import omitKey from 'util/omitKey';

/**
 * Given a column name, partition data into labeled datasets grouped by the unique values of the partition column.
 *
 * Example usage:
 *
 *    partitionDataset(
 *      [
 *        {x: 1, y: 11, label: "a"},
 *        {x: 2, y: 22, label: "a"},
 *        {x: 3, y: 33, label: "b"},
 *        {x: 4, y: 44, label: "b"},
 *      ],
 *      'label'
 *    )
 *
 * The output will be:
 *
 *    [
 *      {
 *        label: 'a',
 *        points: [
 *          {x: 1, y: 11},
 *          {x: 2, y: 22},
 *        ]
 *      },
 *      {
 *        label: 'b',
 *        points: [
 *          {x: 3, y: 33},
 *          {x: 4, y: 44},
 *        ]
 *      },
 *    ]
 *
 * @template DataPoint The type of a row in the dataset, should be a map where each key is the column name.
 * @param dataset Array of rows that are of type DataPoint
 * @param partitionColumn The column name from DataPoint to partition the data on
 *
 * @deprecated This function is implemented with an old version of the DataFrame interface. New uses are discouraged,
 * instead the function should be converted to use the new interface.
 */
// TODO(BNCH-24546): Remove or reimplement data frame utils that use old data frame formats
export default function partitionDataset<DataPoint, PartitionColumn extends keyof DataPoint>(
  dataset: Array<DataPoint>,
  partitionColumn: PartitionColumn
): Array<{
  points: Array<Omit<DataPoint, PartitionColumn>>;
  label: DataPoint[PartitionColumn];
}> {
  const partitionedData: Map<DataPoint[PartitionColumn], Array<DataPoint>> = dataset.reduce(
    (partitionToPoints, dataPoint) => {
      partitionToPoints.set(dataPoint[partitionColumn], [
        ...(partitionToPoints.get(dataPoint[partitionColumn]) || []),
        dataPoint,
      ]);
      return partitionToPoints;
    },
    new Map()
  );

  return Array.from(partitionedData).map(([label, dataset]) => ({
    label,
    points: dataset.map((row) => omitKey(row, partitionColumn)),
  }));
}
